import React, { useState } from "react";
import { Box, Typography } from "@mui/material";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import useEmsProjectsTreeData from "../../../../../../components/Drawers/TreeViewDrawer/treeView/hooks/useEmsProjectsTreeData";
import EmsProjectsTreeView from "../../../../../../components/Drawers/TreeViewDrawer/treeView";
import SummaryPanel from "./SummaryPanel";
import DocumentFilesPanel from "./DocumentFilesPanel";
import FilePreviewPanel from "./FilePreviewPanel";

const TREE_COLUMN_WIDTH = 440;

const EmptyState = () => (
  <Box
    sx={{
      height: "100%",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      color: "text.secondary",
      gap: 1,
    }}
  >
    <AccountTreeIcon sx={{ fontSize: 40, opacity: 0.4 }} />
    <Typography variant="body2">
      Select a plant, WBS, or document from the tree to see it here.
    </Typography>
  </Box>
);

/**
 * Full-page EMS explorer: the same Plant -> WBS -> Documents/Input Data/
 * Estimation/2D Drawings tree as the global AppSidebar's ProjectsTreeSection,
 * but as a left column next to a right-hand content pane instead of navigating
 * away. Selecting a Plant/WBS shows a summary dashboard; selecting a
 * Document shows its SharePoint file/folder browser; clicking a file in
 * either one shows that file's content inline (FilePreviewPanel) with a way
 * back to whatever was showing before. PV Input Data/Estimation/2D Drawing
 * leaves still navigate away (handled inside EmsProjectsTreeView) since
 * those are full editable forms on other pages.
 */
export default function EmsExplorerPage() {
  const treeData = useEmsProjectsTreeData();
  const [selection, setSelection] = useState(null);

  const handleSelectWbs = (plant, wbs) => setSelection({ kind: "wbs", plant, wbs });

  // Remembers what was showing before a file was opened, so "Back" restores
  // it instead of dropping to the empty state.
  const handleOpenFile = (file) =>
    setSelection((prev) => ({ kind: "file", file, returnTo: prev }));
  const handleBackFromFile = () =>
    setSelection((prev) => prev?.returnTo ?? null);

  return (
    <Box sx={{ display: "flex", height: "calc(100vh - 64px)" }}>
      <Box
        sx={{
          width: TREE_COLUMN_WIDTH,
          flexShrink: 0,
          borderRight: "1px solid",
          borderColor: "divider",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <EmsProjectsTreeView
          data={treeData}
          activePlantId={selection?.plant?.id}
          onSelect={setSelection}
        />
      </Box>

      <Box sx={{ flexGrow: 1, minWidth: 0, overflow: "hidden" }}>
        {selection?.kind === "file" ? (
          <FilePreviewPanel file={selection.file} onBack={handleBackFromFile} />
        ) : selection?.kind === "document" ? (
          <DocumentFilesPanel
            plant={selection.plant}
            wbs={selection.wbs}
            doc={selection.doc}
            onOpenFile={handleOpenFile}
          />
        ) : selection?.kind === "plant" || selection?.kind === "wbs" ? (
          <SummaryPanel
            selection={selection}
            treeData={treeData}
            onSelectWbs={handleSelectWbs}
            onOpenFile={handleOpenFile}
          />
        ) : (
          <EmptyState />
        )}
      </Box>
    </Box>
  );
}
